import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueueStore, QueueConfig } from '../../stores/queueStore';
import { lineApi } from '../../services/api';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { config, fetchConfig, updateConfig, resetQueue } = useQueueStore();
  const [formData, setFormData] = useState<Partial<QueueConfig>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [testUserId, setTestUserId] = useState('');

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    if (config) {
      setFormData({
        name: config.name,
        estimatedServeTime: config.estimatedServeTime,
        maxCapacity: config.maxCapacity,
        autoResetDaily: config.autoResetDaily,
        notifyBeforeCount: config.notifyBeforeCount,
        allowOnlineQueue: config.allowOnlineQueue,
        storeAddress: config.storeAddress,
        // LINE 推播
        lineChannelId: config.lineChannelId,
        lineChannelSecret: config.lineChannelSecret,
        lineAccessToken: config.lineAccessToken,
        lineEnabled: config.lineEnabled,
        // LINE Login
        lineLoginChannelId: config.lineLoginChannelId,
        lineLoginChannelSecret: config.lineLoginChannelSecret,
        lineLiffId: config.lineLiffId,
        lineLoginEnabled: config.lineLoginEnabled,
      });
    }
  }, [config]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateConfig(formData);
    } catch (e) {}
    setIsSaving(false);
  };

  const handleReset = async () => {
    if (window.confirm(t('queue.messages.confirmReset'))) {
      await resetQueue();
      fetchConfig();
    }
  };

  const handleTestPush = async () => {
    if (!testUserId.trim()) {
      toast.error(t('queue.settings.lineIntegration.testFailed'));
      return;
    }
    setIsTesting(true);
    try {
      await lineApi.testPush(testUserId.trim());
      toast.success(t('queue.settings.lineIntegration.testSuccess'));
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('queue.settings.lineIntegration.testFailed'));
    }
    setIsTesting(false);
  };

  const handleVerifyToken = async () => {
    if (!formData.lineAccessToken) {
      toast.error(t('queue.settings.lineIntegration.tokenInvalid'));
      return;
    }
    setIsVerifying(true);
    try {
      const res = await lineApi.verifyToken(formData.lineAccessToken);
      if (res.data.valid) {
        toast.success(t('queue.settings.lineIntegration.tokenValid'));
      } else {
        toast.error(t('queue.settings.lineIntegration.tokenInvalid'));
      }
    } catch {
      toast.error(t('queue.settings.lineIntegration.tokenInvalid'));
    }
    setIsVerifying(false);
  };

  const queueUrl = config ? `${window.location.origin}/q/${config.id}` : '';
  const BACKEND_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://127.0.0.1:5007';
  const callbackUrl = `${BACKEND_URL}/api/line/auth/callback`;

  return (
    <div className="container mx-auto px-4 md:px-10 py-6">
      <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {t('navigation.settings')}
        </h1>
      </div>

      {/* 浮動儲存按鈕 */}
      <div className="fixed top-6 right-4 md:right-10 z-30">
        <button
          type="submit"
          disabled={isSaving}
          className={`font-bold py-2 px-4 rounded transition-all duration-200 disabled:opacity-50 ${
            isSaving
              ? 'bg-brand-600 text-white cursor-not-allowed'
              : 'bg-brand-600 hover:bg-brand-700 text-white'
          }`}
        >
          {isSaving ? (
            <span className="flex items-center gap-2">
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
              {t('common.status.saving')}
            </span>
          ) : (
            t('common.save')
          )}
        </button>
      </div>

      {/* 店家資訊 */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('queue.settings.storeInfo')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="storeName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('queue.settings.storeName')}</label>
            <input
              id="storeName"
              name="storeName"
              type="text"
              autoComplete="organization"
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label htmlFor="storeAddress" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('queue.settings.storeAddress')}</label>
            <input
              id="storeAddress"
              name="storeAddress"
              type="text"
              autoComplete="street-address"
              value={formData.storeAddress || ''}
              onChange={(e) => setFormData({ ...formData, storeAddress: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* 排隊設定 */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('queue.settings.queueSettings')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="estimatedServeTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('queue.settings.estimatedServeTime')}</label>
            <div className="flex items-center gap-2">
              <input
                id="estimatedServeTime"
                name="estimatedServeTime"
                type="number"
                autoComplete="off"
                min="1"
                max="120"
                value={formData.estimatedServeTime || 10}
                onChange={(e) => setFormData({ ...formData, estimatedServeTime: parseInt(e.target.value) || 10 })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
              />
              <span className="text-gray-500 dark:text-gray-400 whitespace-nowrap">{t('queue.common.minutes')}</span>
            </div>
          </div>
          <div>
            <label htmlFor="maxCapacity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('queue.settings.maxCapacity')}</label>
            <input
              id="maxCapacity"
              name="maxCapacity"
              type="number"
              autoComplete="off"
              min="0"
              value={formData.maxCapacity || 0}
              onChange={(e) => setFormData({ ...formData, maxCapacity: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
            />
            <p className="text-xs text-gray-400 mt-1">{t('queue.settings.maxCapacityHint')}</p>
          </div>
          <div>
            <label htmlFor="notifyBeforeCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('queue.settings.notifyBeforeCount')}</label>
            <div className="flex items-center gap-2">
              <input
                id="notifyBeforeCount"
                name="notifyBeforeCount"
                type="number"
                autoComplete="off"
                min="1"
                max="10"
                value={formData.notifyBeforeCount || 3}
                onChange={(e) => setFormData({ ...formData, notifyBeforeCount: parseInt(e.target.value) || 3 })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
              />
              <span className="text-gray-500 dark:text-gray-400 whitespace-nowrap">{t('queue.common.groups')}</span>
            </div>
          </div>
        </div>

        {/* 開關設定 */}
        <div className="mt-4 space-y-3">
          <label htmlFor="autoResetDaily" className="flex items-center gap-3">
            <input
              id="autoResetDaily"
              name="autoResetDaily"
              type="checkbox"
              checked={formData.autoResetDaily || false}
              onChange={(e) => setFormData({ ...formData, autoResetDaily: e.target.checked })}
              className="h-4 w-4 text-brand-600 rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">{t('queue.settings.autoResetDaily')}</span>
          </label>
          <label htmlFor="allowOnlineQueue" className="flex items-center gap-3">
            <input
              id="allowOnlineQueue"
              name="allowOnlineQueue"
              type="checkbox"
              checked={formData.allowOnlineQueue || false}
              onChange={(e) => setFormData({ ...formData, allowOnlineQueue: e.target.checked })}
              className="h-4 w-4 text-brand-600 rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">{t('queue.settings.allowOnlineQueue')}</span>
          </label>
        </div>
      </div>

      {/* QR Code */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('queue.settings.qrCode')}</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{t('queue.settings.qrCodeHint')}</p>
        {queueUrl && (
          <div className="flex flex-col items-center gap-4">
            <div className="bg-white p-4 rounded-lg">
              <QRCodeSVG value={queueUrl} size={200} />
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 break-all text-center">{queueUrl}</p>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(queueUrl);
              }}
              className="bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-300 font-bold py-2 px-4 rounded transition-all duration-200"
            >
              {t('queue.settings.copyLink')}
            </button>
          </div>
        )}
      </div>

      {/* LINE 整合 */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('queue.settings.lineIntegration.title')}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          {t('queue.settings.lineIntegration.description')}
        </p>

        {/* LINE 推播通知 */}
        <div className="mb-8">
          <h3 className="text-md font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#06C755]"></span>
            {t('queue.settings.lineIntegration.messagingApi')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="lineChannelId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Channel ID</label>
              <input
                id="lineChannelId"
                name="lineChannelId"
                type="text"
                autoComplete="off"
                value={formData.lineChannelId || ''}
                onChange={(e) => setFormData({ ...formData, lineChannelId: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label htmlFor="lineChannelSecret" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Channel Secret</label>
              <input
                id="lineChannelSecret"
                name="lineChannelSecret"
                type="password"
                autoComplete="off"
                value={formData.lineChannelSecret || ''}
                onChange={(e) => setFormData({ ...formData, lineChannelSecret: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="lineAccessToken" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Channel Access Token</label>
              <input
                id="lineAccessToken"
                name="lineAccessToken"
                type="password"
                autoComplete="off"
                value={formData.lineAccessToken || ''}
                onChange={(e) => setFormData({ ...formData, lineAccessToken: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* 啟用開關 + 操作按鈕 */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <label htmlFor="lineEnabled" className="flex items-center gap-2">
              <input
                id="lineEnabled"
                name="lineEnabled"
                type="checkbox"
                checked={formData.lineEnabled || false}
                onChange={(e) => setFormData({ ...formData, lineEnabled: e.target.checked })}
                className="h-4 w-4 text-[#06C755] rounded"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {t('queue.settings.lineIntegration.enableNotifications')}
              </span>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleVerifyToken}
              disabled={isVerifying || !formData.lineAccessToken}
              className="bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 disabled:text-gray-500 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded transition-all duration-200"
            >
              {isVerifying ? '...' : t('queue.settings.lineIntegration.verifyToken')}
            </button>
            <input
              id="testUserId"
              name="testUserId"
              type="text"
              autoComplete="off"
              value={testUserId}
              onChange={(e) => setTestUserId(e.target.value)}
              placeholder="U1234567890abcdef..."
              aria-label="LINE User ID"
              className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm w-48"
            />
            <button
              type="button"
              onClick={handleTestPush}
              disabled={isTesting || !formData.lineAccessToken}
              className="font-bold py-2 px-4 rounded transition-all duration-200 disabled:bg-slate-300 disabled:text-gray-500 disabled:cursor-not-allowed text-white"
              style={{ backgroundColor: isTesting || !formData.lineAccessToken ? undefined : '#06C755' }}
            >
              {isTesting ? t('queue.settings.lineIntegration.testing') : t('queue.settings.lineIntegration.testPush')}
            </button>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {t('queue.settings.lineIntegration.userIdHint')}
          </p>
        </div>

        {/* LINE Login */}
        <div>
          <h3 className="text-md font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#06C755]"></span>
            {t('queue.settings.lineIntegration.lineLogin')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            {t('queue.settings.lineIntegration.lineLoginDescription')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="lineLoginChannelId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Login Channel ID</label>
              <input
                id="lineLoginChannelId"
                name="lineLoginChannelId"
                type="text"
                autoComplete="off"
                value={formData.lineLoginChannelId || ''}
                onChange={(e) => setFormData({ ...formData, lineLoginChannelId: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label htmlFor="lineLoginChannelSecret" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Login Channel Secret</label>
              <input
                id="lineLoginChannelSecret"
                name="lineLoginChannelSecret"
                type="password"
                autoComplete="off"
                value={formData.lineLoginChannelSecret || ''}
                onChange={(e) => setFormData({ ...formData, lineLoginChannelSecret: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label htmlFor="lineLiffId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                LIFF ID <span className="text-gray-400 text-xs">({t('queue.settings.lineIntegration.optional')})</span>
              </label>
              <input
                id="lineLiffId"
                name="lineLiffId"
                type="text"
                autoComplete="off"
                value={formData.lineLiffId || ''}
                onChange={(e) => setFormData({ ...formData, lineLiffId: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* 啟用開關 */}
          <label htmlFor="lineLoginEnabled" className="flex items-center gap-2 mb-4">
            <input
              id="lineLoginEnabled"
              name="lineLoginEnabled"
              type="checkbox"
              checked={formData.lineLoginEnabled || false}
              onChange={(e) => setFormData({ ...formData, lineLoginEnabled: e.target.checked })}
              className="h-4 w-4 text-[#06C755] rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t('queue.settings.lineIntegration.enableLineLogin')}
            </span>
          </label>

          {/* Callback URL 顯示 */}
          <div className="bg-slate-50 dark:bg-slate-700/50 rounded-md p-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('queue.settings.lineIntegration.callbackUrl')}
            </label>
            <div className="flex items-center gap-2">
              <code className="text-sm text-gray-600 dark:text-gray-400 break-all flex-1">{callbackUrl}</code>
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(callbackUrl)}
                className="text-brand-600 hover:text-brand-700 text-sm whitespace-nowrap"
              >
                {t('queue.settings.copyLink')}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              {t('queue.settings.lineIntegration.callbackUrlHint')}
            </p>
            <a
              href={formData.lineLoginChannelId
                ? `https://developers.line.biz/console/channel/${formData.lineLoginChannelId}/line-login`
                : 'https://developers.line.biz/console/'}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-2 text-sm text-brand-600 hover:text-brand-700 underline"
            >
              {t('queue.settings.lineIntegration.openConsole')}
            </a>
          </div>
        </div>
      </div>

      {/* 重置排隊 */}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={handleReset}
          className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded transition-all duration-200"
        >
          {t('queue.actions.resetQueue')}
        </button>
      </div>

      </form>
    </div>
  );
};

export default SettingsPage;
